import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { instance as axiosInstance } from '../../../services/axiosInstance';
import AnalyticsService from '../../../services/analytics/AnalyticsService';
import Header from '../../../components/Header';
import { Main, MainContainer } from '../../operational/request/resume/RequestResume';
import { BREADCRUMB_CADASTRO } from '../../../utils/breadcrumbCadastro';
import { MOCK_METADATA } from './mockData';
import ConditionRow from './ConditionRow';
import {
  isConditionValid,
  isFormDataValid,
  arePareceresValid,
  getConditionErrors,
  getParecerValidationErrors,
  getParecerSectionHint,
  getParecerSectionErrorMessage,
} from './ruleValidation';

const COMPOUND_BOOLEAN_FIELDS = [
  'negativacao',
  'acoes_despejo',
  'acoes_criminais',
  'acoes_outras_reu',
  'acoes_outras_autor'
];

const TENANT_PARECER_PREFIX = 'Inquilino(s) considerado(s):';
const GUARANTOR_PARECER_PREFIX = 'Fiador(es) considerado(s):';

const isNegativacaoValorEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

const isNegativacaoPercentualEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

const isOrigemRendaSomaRendaEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

// Normaliza condições ao carregar regra existente (compatibilidade com formato antigo)
const normalizeConditionsForEdit = (conditions) => {
  return (conditions || []).map(condition => {
    if (condition.field === 'renda_soma_por_origem') {
      const origens = (condition.sub_conditions && condition.sub_conditions.origens) || [];
      const migrated = {
        field: 'origem_renda',
        operator: 'in',
        value: origens.map(v => String(v)),
        sub_conditions: {
          soma_renda: {
            enabled: true,
            operator: condition.operator,
            value: condition.value
          }
        }
      };
      return migrated;
    }

    if (COMPOUND_BOOLEAN_FIELDS.includes(condition.field)) {
      const normalized = {
        ...condition,
        value: Array.isArray(condition.value)
          ? condition.value
          : typeof condition.value === 'boolean'
            ? [condition.value]
            : []
      };

      if (condition.field === 'negativacao' && condition.sub_conditions) {
        const subConditions = { ...condition.sub_conditions };
        ['valor', 'valor_percentual'].forEach((key) => {
          const sub = subConditions[key];
          if (!sub || sub.enabled !== undefined) return;
          const hasData = sub.operator ||
            (sub.value !== '' && sub.value !== null && sub.value !== undefined);
          if (hasData) {
            subConditions[key] = { ...sub, enabled: true };
          }
        });
        normalized.sub_conditions = subConditions;
      }

      return normalized;
    }

    if (condition.field !== 'origem_renda') {
      return condition;
    }

    const normalized = { ...condition };

    if (condition.operator === 'is' && !Array.isArray(condition.value)) {
      normalized.operator = 'in';
      normalized.value = condition.value !== '' && condition.value !== null && condition.value !== undefined
        ? [String(condition.value)]
        : [];
    } else if (Array.isArray(condition.value)) {
      normalized.value = condition.value.map(v => String(v));
    }

    if (condition.sub_conditions && condition.sub_conditions.soma_renda) {
      const somaRenda = { ...condition.sub_conditions.soma_renda };
      if (somaRenda.enabled === undefined) {
        const hasData = somaRenda.operator ||
          (somaRenda.value !== '' && somaRenda.value !== null && somaRenda.value !== undefined);
        if (hasData) {
          somaRenda.enabled = true;
        }
      }
      normalized.sub_conditions = {
        ...condition.sub_conditions,
        soma_renda: somaRenda
      };
    }

    return normalized;
  });
};

// Função para preparar condições: converte valores string para tipos apropriados
const prepareConditionsForSubmit = (conditions) => {
  return conditions.map(condition => {
    const preparedCondition = { ...condition };

    if (condition.field === 'origem_renda' && Array.isArray(condition.value)) {
      preparedCondition.value = condition.value.map(v => parseInt(v, 10));
    }

    if (COMPOUND_BOOLEAN_FIELDS.includes(condition.field)) {
      preparedCondition.value = Array.isArray(condition.value)
        ? condition.value
        : typeof condition.value === 'boolean'
          ? [condition.value]
          : [];

      if (!preparedCondition.value.includes(true)) {
        delete preparedCondition.sub_conditions;
      }
    }
    
    // Tratar sub_conditions também
    if (preparedCondition.sub_conditions) {
      const subConditions = preparedCondition.sub_conditions;

      if (condition.field === 'origem_renda') {
        if (isOrigemRendaSomaRendaEnabled(subConditions.soma_renda)) {
          preparedCondition.sub_conditions = {
            soma_renda: {
              enabled: true,
              operator: subConditions.soma_renda.operator,
              value: subConditions.soma_renda.value
            }
          };
        } else {
          delete preparedCondition.sub_conditions;
        }
      } else if (condition.field === 'negativacao') {
        preparedCondition.sub_conditions = {};

        if (subConditions.quantidade) {
          preparedCondition.sub_conditions.quantidade = {
            operator: subConditions.quantidade.operator,
            value: subConditions.quantidade.value
          };
        }

        if (isNegativacaoValorEnabled(subConditions.valor)) {
          preparedCondition.sub_conditions.valor = {
            enabled: true,
            operator: subConditions.valor.operator,
            value: subConditions.valor.value
          };
        }

        if (isNegativacaoPercentualEnabled(subConditions.valor_percentual)) {
          preparedCondition.sub_conditions.valor_percentual = {
            enabled: true,
            operator: subConditions.valor_percentual.operator,
            value: subConditions.valor_percentual.value
          };
        }
      } else {
        preparedCondition.sub_conditions = {};
        Object.keys(subConditions).forEach(subFieldId => {
          const subCondition = subConditions[subFieldId];
          preparedCondition.sub_conditions[subFieldId] = {
            ...subCondition,
            value: subCondition.value !== '' && subCondition.value !== null && subCondition.value !== undefined
              ? subCondition.value
              : subCondition.value
          };
        });
      }
    }
    
    return preparedCondition;
  });
};

class RuleEditor extends Component {
  constructor(props) {
    super(props);
    
    const editingRule = props.location.state && props.location.state.rule;
    
    this.state = {
      formData: {
        name: editingRule ? editingRule.name || '' : '',
        tenant_recommendation_text: editingRule ? editingRule.tenant_recommendation_text || '' : '',
        guarantor_recommendation_text: editingRule ? editingRule.guarantor_recommendation_text || '' : '',
        is_active: editingRule ? (editingRule.is_active !== undefined ? editingRule.is_active : true) : true,
        conditions: editingRule ? normalizeConditionsForEdit(editingRule.conditions || []) : []
      },
      validationAttempted: false
    };

    this.startedAt = Date.now();
    this.hasSavedRule = false;
    this.hasTrackedAbandonment = false;
  }

  componentWillUnmount() {
    this.trackCreationAbandoned();
  }

  isNewRule = () => {
    const editingRule = this.props.location.state && this.props.location.state.rule;
    return !editingRule;
  }

  getSelectedVariableLabels = () => {
    const { formData } = this.state;

    const variableNames = formData.conditions
      .map(c => {
        const field = MOCK_METADATA.fields.find(f => f.id === c.field);
        return field ? field.label : null;
      })
      .filter(Boolean);

    return Array.from(new Set(variableNames));
  }

  handleVariableSelected = (variableName) => {
    if (!variableName) return;

    AnalyticsService.capture('customization_variable_selected', {
      is_new_rule: this.isNewRule(),
      variable_name: variableName
    });
  }

  trackCreationAbandoned = () => {
    if (this.hasSavedRule || this.hasTrackedAbandonment) return;

    const hasVariable = this.state.formData.conditions.some(c => !!c.field);
    const timeSpentSeconds = Math.max(0, Math.round((Date.now() - this.startedAt) / 1000));

    AnalyticsService.capture('customization_creation_abandoned', {
      is_new_rule: this.isNewRule(),
      time_spent_seconds: timeSpentSeconds,
      has_variable: hasVariable
    });

    this.hasTrackedAbandonment = true;
  }

  handleAddCondition = () => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        conditions: [...prevState.formData.conditions, { field: '', operator: '', value: '' }]
      }
    }));
  }

  handleConditionChange = (index, updatedCondition) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        conditions: prevState.formData.conditions.map((c, i) => i === index ? updatedCondition : c)
      }
    }));
  }

  handleRemoveCondition = (index) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        conditions: prevState.formData.conditions.filter((_, i) => i !== index)
      }
    }));
  }

  handleInputChange = (field, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }));
  }

  handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormDataValid(this.state.formData)) {
      this.setState({ validationAttempted: true });
      return;
    }
    
    const { formData } = this.state;
    const editingRule = this.props.location.state && this.props.location.state.rule;
    
    try {
      const URL = '/api/administrador/regras-laudo';
      const token = this.props.login.user.token.access_token;
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (editingRule) {
        // Edição: manter prioridade existente
        const data = {
          nome: formData.name,
          prioridade: editingRule.priority,
          ativa: formData.is_active,
          texto_parecer_inquilinos: formData.tenant_recommendation_text,
          texto_parecer_fiadores: formData.guarantor_recommendation_text,
          condicoes: prepareConditionsForSubmit(formData.conditions)
        };
        
        const result = await axiosInstance.put(`${URL}/${editingRule.id}`, data, { headers });
        
        if (result.status === 401) {
          toastr.error('Autenticação', 'Sua sessão expirou. Faça login novamente.');
          this.props.history.push('/auth');
          return;
        }
        
        if (result.status !== 200) {
          const errorData = result.data && result.data.message;
          if (typeof errorData === 'object') {
            const errorMessages = Object.values(errorData).flat().join(', ');
            toastr.error('Erro', errorMessages);
          } else {
            toastr.error('Erro', errorData || 'Erro ao atualizar regra. Tente novamente.');
          }
          return;
        }
        
        toastr.success('Sucesso', 'Regra atualizada com sucesso!');
      } else {
        // Criação: buscar quantidade atual de regras para definir prioridade
        const listResult = await axiosInstance.get(URL, { headers });
        
        if (listResult.status === 401) {
          toastr.error('Autenticação', 'Sua sessão expirou. Faça login novamente.');
          this.props.history.push('/auth');
          return;
        }
        
        const data = {
          nome: formData.name,
          texto_parecer_inquilinos: formData.tenant_recommendation_text,
          texto_parecer_fiadores: formData.guarantor_recommendation_text,
          condicoes: prepareConditionsForSubmit(formData.conditions)
        };
        
        const result = await axiosInstance.post(URL, data, { headers });
        
        if (result.status === 401) {
          toastr.error('Autenticação', 'Sua sessão expirou. Faça login novamente.');
          this.props.history.push('/auth');
          return;
        }
        
        if (result.status !== 201 && result.status !== 200) {
          const errorData = result.data && result.data.message;
          if (typeof errorData === 'object') {
            const errorMessages = Object.values(errorData).flat().join(', ');
            toastr.error('Erro', errorMessages);
          } else {
            toastr.error('Erro', errorData || 'Erro ao criar regra. Tente novamente.');
          }
          return;
        }
        
        toastr.success('Sucesso', 'Regra criada com sucesso!');
      }

      const variables = this.getSelectedVariableLabels();

      AnalyticsService.capture('customization_saved', {
        is_new_rule: this.isNewRule(),
        variable_count: variables.length,
        variables
      });

      this.hasSavedRule = true;
      
      this.props.history.push('/intranet/cadastro/customizar-pareceres');
    } catch (error) {
      console.error('Erro ao salvar regra:', error);
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : 'Erro ao salvar regra. Tente novamente.';
      toastr.error('Erro', errorMessage);
    }
  }

  handleCancel = () => {
    this.props.history.push('/intranet/cadastro/customizar-pareceres');
  }

  isFormValid = () => isFormDataValid(this.state.formData);

  getSummary = () => {
    const count = this.state.formData.conditions.length;
    if (count === 0) return 'Nenhum critério';
    
    const fieldNames = this.state.formData.conditions
      .slice(0, 3)
      .map(c => {
        const field = MOCK_METADATA.fields.find(f => f.id === c.field);
        return field ? field.label : c.field;
      })
      .filter(Boolean);
    
    if (fieldNames.length === 0) return `${count} critério(s)`;
    
    const summary = fieldNames.join(', ');
    const remaining = count - fieldNames.length;
    return remaining > 0 ? `${summary} +${remaining} outro(s)` : summary;
  }

  render() {
    const { formData, validationAttempted } = this.state;
    const editingRule = this.props.location.state && this.props.location.state.rule;
    const pageTitle = editingRule ? 'EDITAR REGRA' : 'NOVA REGRA';
    const parecerErrors = getParecerValidationErrors(formData);
    const { requiresTenant, requiresGuarantor, requiresAny } = parecerErrors;
    const pareceresValid = arePareceresValid(formData);
    const showTenantParecerError = validationAttempted && parecerErrors.tenant;
    const showGuarantorParecerError = validationAttempted && parecerErrors.guarantor;
    const showNameError = validationAttempted && !formData.name.trim();
    const conditionsSectionError = validationAttempted && (
      formData.conditions.length === 0 ||
      !formData.conditions.every(isConditionValid)
    );
    const parecerSectionError = validationAttempted && !pareceresValid;
    const formIsValid = this.isFormValid();

    return (
      <Main>
        <Header
          title={pageTitle}
          breadcrumb={BREADCRUMB_CADASTRO}
          actualLink={editingRule ? 'Editar Regra' : 'Nova Regra'}
        />
        <MainContainer>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '8px' }}>
              {editingRule ? 'Editar Regra' : 'Nova Regra'}
            </h1>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Defina os critérios e o texto que sera exibido no laudo quando esta regra for aplicada.
            </p>
          </div>

        <form onSubmit={this.handleSubmit}>
          <section style={{ 
            backgroundColor: '#fff', 
            padding: '24px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            border: '1px solid #e0e0e0'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                backgroundColor: '#0066cc',
                color: '#fff',
                borderRadius: '50%',
                fontSize: '12px',
                fontWeight: '700',
                lineHeight: '1',
                flexShrink: 0
              }}>
                1
              </span>
              Identificação
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Nome da Regra *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => this.handleInputChange('name', e.target.value)}
                placeholder="Ex: Aprovação PF Risco Baixo"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1px solid ${showNameError ? '#dc3545' : '#ddd'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: showNameError ? '#fff5f5' : '#fff'
                }}
              />
              {showNameError && (
                <p role="alert" style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px' }}>
                  Informe o nome da regra.
                </p>
              )}
            </div>

            {/* <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => this.handleInputChange('is_active', e.target.checked)}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Regra Ativa</span>
              </label>
            </div> */}
          </section>

          <section style={{ 
            backgroundColor: conditionsSectionError ? '#fff5f5' : '#fff', 
            padding: '24px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            border: `1px solid ${conditionsSectionError ? '#dc3545' : '#e0e0e0'}`
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#0066cc',
                    color: '#fff',
                    borderRadius: '50%',
                    fontSize: '12px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    2
                  </span>
                  Critérios de Aprovação
                </h2>
              </div>
            </div>

            {conditionsSectionError && (
              <p role="alert" style={{ color: '#dc3545', fontSize: '13px', marginBottom: '16px' }}>
                {formData.conditions.length === 0
                  ? 'Adicione pelo menos um critério de aprovação.'
                  : 'Preencha todos os campos obrigatórios dos critérios destacados em vermelho.'}
              </p>
            )}

            {formData.conditions.length === 0 ? (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                backgroundColor: conditionsSectionError ? '#fff5f5' : '#f9f9f9', 
                borderRadius: '4px',
                border: `2px dashed ${conditionsSectionError ? '#dc3545' : '#ddd'}`
              }}>
                <p style={{ color: '#999', fontSize: '14px' }}>
                  Nenhum critério adicionado. Clique em "Adicionar Critério" para começar.
                </p>
              </div>
            ) : (
              <div>
                {formData.conditions.map((condition, index) => (
                  <ConditionRow
                    key={index}
                    condition={condition}
                    index={index}
                    metadata={MOCK_METADATA}
                    conditions={formData.conditions}
                    onChange={this.handleConditionChange}
                    onRemove={this.handleRemoveCondition}
                    onVariableSelected={this.handleVariableSelected}
                    showValidation={validationAttempted}
                    isValid={isConditionValid(condition)}
                    validationErrors={getConditionErrors(condition)}
                  />
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                type="button"
                onClick={this.handleAddCondition}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0066cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#0052a3'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#0066cc'}
              >
                + Adicionar Critério
              </button>
            </div>
          </section>

          <section style={{ 
            backgroundColor: parecerSectionError ? '#fff5f5' : '#fff', 
            padding: '24px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            border: `1px solid ${parecerSectionError ? '#dc3545' : '#e0e0e0'}`
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                backgroundColor: '#0066cc',
                color: '#fff',
                borderRadius: '50%',
                fontSize: '12px',
                fontWeight: '700',
                flexShrink: 0
              }}>
                3
              </span>
              Texto do Resultado no Laudo
            </h2>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
              {getParecerSectionHint(formData.conditions)}
            </p>

            {parecerSectionError && (
              <p
                role="alert"
                style={{ color: '#dc3545', fontSize: '13px', marginBottom: '16px' }}
              >
                {getParecerSectionErrorMessage(formData)}
              </p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Frase exibida quando esta regra for aplicada (Inquilinos)
                  {requiresAny ? (
                    <span style={{ color: '#999', fontWeight: '400' }}> (opcional se Fiadores estiver preenchido)</span>
                  ) : requiresTenant ? (
                    <span style={{ color: '#dc3545' }}> *</span>
                  ) : (
                    <span style={{ color: '#999', fontWeight: '400' }}> (opcional)</span>
                  )}
                </label>
                <textarea
                  maxLength={500}
                  value={formData.tenant_recommendation_text}
                  onChange={(e) => this.handleInputChange('tenant_recommendation_text', e.target.value)}
                  placeholder="Texto que será exibido no laudo para inquilinos..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '10px',
                    textIndent: 208,
                    border: `1px solid ${showTenantParecerError ? '#dc3545' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: showTenantParecerError ? '#fff5f5' : '#fff'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 38,
                    left: 13,
                    fontSize: '14px',
                    color: '#545454',
                    backgroundColor: showTenantParecerError ? '#fff5f5' : '#f5f5f5',
                    pointerEvents: 'none',
                  }}
                >
                  {TENANT_PARECER_PREFIX}
                </div>
                {showTenantParecerError && !requiresAny && (
                  <p role="alert" style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px' }}>
                    Informe o parecer para Inquilinos.
                  </p>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <span style={{ color: '#0066cc' }}>
                    Os nomes dos pretendentes são inseridos automaticamente no laudo.
                  </span>
                  <span style={{ 
                    fontWeight: '600',
                    color: formData.tenant_recommendation_text.length > 400 ? '#ff9800' : '#666'
                  }}>
                    {formData.tenant_recommendation_text.length}/500
                  </span>
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Frase exibida quando esta regra for aplicada (Fiadores)
                  {requiresAny ? (
                    <span style={{ color: '#999', fontWeight: '400' }}> (opcional se Inquilinos estiver preenchido)</span>
                  ) : requiresGuarantor ? (
                    <span style={{ color: '#dc3545' }}> *</span>
                  ) : (
                    <span style={{ color: '#999', fontWeight: '400' }}> (opcional)</span>
                  )}
                </label>
                <textarea
                  maxLength={500}
                  value={formData.guarantor_recommendation_text}
                  onChange={(e) => this.handleInputChange('guarantor_recommendation_text', e.target.value)}
                  placeholder="Texto que será exibido no laudo para fiadores..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '10px',
                    textIndent: 198,
                    border: `1px solid ${showGuarantorParecerError ? '#dc3545' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: showGuarantorParecerError ? '#fff5f5' : '#fff'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 38,
                    left: 13,
                    fontSize: '14px',
                    color: '#545454',
                    backgroundColor: showGuarantorParecerError ? '#fff5f5' : '#f5f5f5',
                    pointerEvents: 'none',
                  }}
                >
                  {GUARANTOR_PARECER_PREFIX}
                </div>
                {showGuarantorParecerError && !requiresAny && (
                  <p role="alert" style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px' }}>
                    Informe o parecer para Fiadores.
                  </p>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <span style={{ color: '#0066cc' }}>
                    Os nomes dos pretendentes são inseridos automaticamente no laudo.
                  </span>
                  <span style={{ 
                    fontWeight: '600',
                    color: formData.guarantor_recommendation_text.length > 400 ? '#ff9800' : '#666'
                  }}>
                    {formData.guarantor_recommendation_text.length}/500
                  </span>
                </div>
              </div>
            </div>
          </section>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginBottom: '60px' }}>
            <button
              type="button"
              onClick={this.handleCancel}
              style={{
                padding: '12px 24px',
                backgroundColor: '#fff',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                padding: '12px 24px',
                backgroundColor: formIsValid ? '#28a745' : '#ccc',
                color: formIsValid ? '#fff' : '#999',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: formIsValid ? 1 : 0.6
              }}
              onMouseOver={(e) => {
                if (formIsValid) {
                  e.target.style.backgroundColor = '#218838';
                }
              }}
              onMouseOut={(e) => {
                if (formIsValid) {
                  e.target.style.backgroundColor = '#28a745';
                }
              }}
            >
              Salvar Regra
            </button>
          </div>
        </form>
        </MainContainer>
      </Main>
    );
  }
}

const mapStateToProps = (state) => ({
  login: state.login,
});

export default connect(mapStateToProps)(withRouter(RuleEditor));
